import { useTranslation } from '@/shared/web/i18n/LanguageProvider';
import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../features/login/ux/hooks/useAuth';
import { Skeleton } from '@/web/components/ui/skeleton';
import { Button } from '@/web/components/ui/button';
import { usePermissions } from '@/web/hooks/usePermissions';

/**
 * Gate for authenticated routes. Waits for the session check before deciding,
 * otherwise a refresh would bounce a signed-in user to /login.
 *
 * The fallback mirrors the pipeline's own layout so the transition into the
 * real UI is a fill, not a jump.
 */
export function ProtectedRoute({
  children,
  /**
   * The permission this route needs, e.g. `revenue.view`. Omitted means "any
   * signed-in user", which is now only correct for screens everyone can see.
   */
  requires,
}: {
  children: ReactNode;
  requires?: string;
}) {
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const { can, homeRoute } = usePermissions();

  if (isLoading) {
    return (
      <div className="bg-background flex h-screen overflow-hidden">
        <div className="bg-sidebar border-border hidden w-60 shrink-0 border-r md:block" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-col">
            <div className="border-border flex items-center justify-between border-b px-5 py-6 sm:px-7 sm:py-7">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-7 w-40" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="squircle-sm h-8 w-32" />
            </div>
            <div className="border-border grid grid-cols-2 border-t md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="border-border flex flex-col gap-2 border-r p-4">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-8 w-12" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const pair = location.pathname === '/chrome-extension' && /^#pair=([0-9a-f-]{36})$/i.exec(location.hash)?.[1];
    return <Navigate to={pair ? `/login?extensionPair=${pair}` : '/login'} replace />;
  }

  if (requires && !can(requires)) {
    /**
     * Send them somewhere they can actually be, rather than showing a wall.
     *
     * This path is reached by a stale bookmark or a link from a colleague with
     * broader access — both cases where the useful outcome is "here is your
     * app", not "access denied". `replace` keeps the forbidden URL out of the
     * history so Back does not bounce them straight into it again.
     *
     * A role that grants nothing has no home to go to, so that one case gets a
     * sentence instead of a redirect loop.
     */
    return homeRoute ? <Navigate to={homeRoute} replace /> : <NoAccess />;
  }

  return <>{children}</>;
}

/**
 * The empty-role screen.
 *
 * Only reachable when someone's role has been stripped of everything, or when
 * their role was deleted out from under them. It names the cause, because the
 * alternative — a blank app — reads as an outage and gets reported as one.
 */
function NoAccess() {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <div className="bg-background flex h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-xl font-semibold tracking-tight">No access yet</h1>
      <p className="text-muted-foreground max-w-md text-sm">
        Your account is active but your role does not include any screens yet. Ask an admin to
        assign you a role in Settings &rarr; Team &amp; Roles.
      </p>
      <Button variant="outline" size="sm" className="squircle-sm" onClick={() => void signOut()}>
        {t("Sign out")}
      </Button>
    </div>
  );
}
